import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePublicCatalogData } from '@/components/PublicCatalogData';
import { excursionsService } from '../api/services.js';

const DEFAULT_TTL_MS = 2 * 60 * 1000;
const cache = new Map();

const buildKey = (params) => {
  if (!params || Object.keys(params).length === 0) {
    return 'excursions:list';
  }
  return `excursions:list:${JSON.stringify(params)}`;
};

const extractData = (response) => {
  if (!response) return null;
  return response.data?.data || response.data || [];
};

const getCacheEntry = (key) => cache.get(key);

const setCacheEntry = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    inFlight: null
  });
};

const setInFlight = (key, promise) => {
  const entry = cache.get(key) || { data: null, timestamp: 0, inFlight: null };
  entry.inFlight = promise;
  cache.set(key, entry);
};

const clearInFlight = (key) => {
  const entry = cache.get(key);
  if (entry) {
    entry.inFlight = null;
    cache.set(key, entry);
  }
};

const shouldRevalidate = (entry, ttlMs) => {
  if (!entry || !entry.data) return true;
  return Date.now() - entry.timestamp > ttlMs;
};

const useExcursionsList = (params = null, options = {}) => {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const catalog = usePublicCatalogData();
  const contextList = !params && catalog?.excursions != null ? catalog.excursions : null;
  const cacheKey = useMemo(() => buildKey(params), [params]);
  const cached = getCacheEntry(cacheKey);
  const [response, setResponse] = useState(() => {
    if (contextList != null) return { data: { data: contextList, success: true } };
    return cached?.data ?? null;
  });
  const [loading, setLoading] = useState(contextList == null && !cached?.data);
  const [error, setError] = useState(null);

  const fetchAndUpdate = useCallback(
    (force = false) => {
      const entry = getCacheEntry(cacheKey);
      if (!force && entry?.inFlight) {
        entry.inFlight
          .then((res) => {
            setResponse(res);
            setError(null);
            setLoading(false);
            return res;
          })
          .catch((err) => {
            setError(err);
            setLoading(false);
            throw err;
          });
        return entry.inFlight;
      }

      const request = excursionsService.getAll(params);
      setInFlight(cacheKey, request);

      request
        .then((res) => {
          setCacheEntry(cacheKey, res);
          setResponse(res);
          setError(null);
          setLoading(false);
          return res;
        })
        .catch((err) => {
          setError(err);
          setLoading(false);
          throw err;
        })
        .finally(() => {
          clearInFlight(cacheKey);
        });

      return request;
    },
    [cacheKey, params]
  );

  useEffect(() => {
    let active = true;
    if (contextList != null) {
      setResponse({ data: { data: contextList, success: true } });
      setLoading(false);
      return () => {
        active = false;
      };
    }
    const entry = getCacheEntry(cacheKey);

    if (entry?.data) {
      setResponse(entry.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (shouldRevalidate(entry, ttlMs)) {
      fetchAndUpdate().catch(() => {
        if (!active) return;
      });
    }

    return () => {
      active = false;
    };
  }, [cacheKey, ttlMs, fetchAndUpdate, contextList]);

  const data = useMemo(() => extractData(response), [response]);

  return {
    response,
    data,
    loading,
    error,
    refresh: () => fetchAndUpdate(true)
  };
};

export default useExcursionsList;
